import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CreateActionTile from "../../CreateActionTile/CreateActionTile";
import Loader from "../../loader/Loader";
import { REQUEST_LOADING_STATES } from "../../../helpers/constant";
import SelectedIcon from "../../../assets/icons/signup-selected.svg";
import "./index.less";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchTrackingLinks } from "../../../redux/trackingLinks/thunks/trackingLinksThunks";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import useFetchUserInfos from "../../../hooks/userHooks/useFetchUserInfos";
import {
  selectTrackingLinkLoading,
  selectTrackingLinksByCreator,
  selectTrackingLinksByCreatorAndStatus,
  selectTrackingLinksLoadingByCreatorAndStatus,
} from "../../../redux/trackingLinks/selectors/trackingLinksSelectors";
import AppHeader from "../../AppHeader/AppHeader";
import TrackingLinksList, { TrackingLinkItem } from "../TrackingLinkList/TrackingLinkList";
import dayjs from "dayjs";

export type TrackingLinksTabsProps = { activeTab: string; onSelect: (category: string) => void };

const TrackingLinksTabs: React.FC<TrackingLinksTabsProps> = ({ activeTab, onSelect }) => {
  const { t } = useTranslation();

  const options: { key: string; label: string }[] = [
    { key: "active", label: t("active-links") },
    { key: "inactive", label: t("inactive-links") },
  ];

  return (
    <div className="track-link-tab-wrapper">
      <div className="track-link-switch-container">
        {options.map((opt) => (
          <div
            key={opt.key}
            className={`track-link-switch ${activeTab === opt.key ? "selected" : ""}`}
            onClick={() => onSelect(opt.key)}
            role="button"
            tabIndex={0}
            aria-pressed={activeTab === opt.key}
          >
            <div className="tl-switch-content">
              {activeTab === opt.key && <img src={SelectedIcon} className="tl-selected-icon" alt="Selected" />}
              <span className={`track-link-switch-text ${activeTab === opt.key ? "white" : ""}`}>{opt.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrackingLinksComponent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentUser: currentCreator } = useFetchUserInfos();
  const [activeCategory, setActiveCategory] = useState<string>("active"); // active | inactive
  const trackingLinks = useAppSelector(selectTrackingLinksByCreator(currentCreator?.id!));

  const loading = useAppSelector(selectTrackingLinkLoading);
  const trackingLinksEntry = useAppSelector(selectTrackingLinksByCreatorAndStatus(currentCreator?.id!, activeCategory));
  const trackingLinksLoading = useAppSelector(selectTrackingLinksLoadingByCreatorAndStatus(currentCreator?.id!, activeCategory));

  const listRef = useRef<HTMLDivElement | null>(null);

  const isActiveFetched = useRef<boolean>(false);
  const isInactiveFetched = useRef<boolean>(false);

  useEffect(() => {
    const isFetched = activeCategory === "active" ? isActiveFetched : isInactiveFetched;

    if (currentCreator && trackingLinksEntry?.items?.length === 0 && !isFetched.current) {
      dispatch(fetchTrackingLinks({ creatorId: currentCreator.id!, status: activeCategory }));
      isFetched.current = true;
    }
  }, [dispatch, currentCreator, activeCategory, trackingLinksEntry?.items]);


    const hasTrackingLinks = trackingLinksEntry?.items?.length > 0;
  const items: TrackingLinkItem[] = useMemo(
    () =>
      (trackingLinksEntry.items || []).map((s: any) => ({
        id: s._id,
        title: s.title,
        createdAt: s.createdAt ? dayjs(s.createdAt).format("DD MMMM YYYY") : undefined,
        totalNumberOfClicks: s.totalNumberOfClicks ?? 0,
        status: activeCategory,
      })),
    [trackingLinksEntry.items, activeCategory]
  );

  return (
    <div className="tracking-link-view">
      <AppHeader title={t("tracking-links")} backButtonDestination="/common/setting" />

      <main className="tracking-link-content">
        <CreateActionTile labelKey="create-new-tracking-link-text" onClick={() => navigate("/creator/tracking-links/create")} />
        {loading === REQUEST_LOADING_STATES.PENDING || (loading === REQUEST_LOADING_STATES.IDLE && !hasTrackingLinks) ? (
          <div className="loader-wrapper">
            <Loader />
          </div>
        ) : (
          <div className="tracking-link-panel">
            <TrackingLinksTabs activeTab={activeCategory} onSelect={setActiveCategory} />
            {trackingLinksLoading === REQUEST_LOADING_STATES.PENDING ? (
              <div className="loader-tracking-link-wrapper">
                <Loader />
              </div>
            ) : items?.length > 0 ? (
              <TrackingLinksList items={items} listRef={listRef} />
            ) : (
              <div className="no-tracking-link-card" role="note" aria-live="polite">
                {t("no-tracking-links-created")}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TrackingLinksComponent;
